import { Dropdown, Icon, css } from '@appd/uik';
import { VisualizationTypeOption, EditableComponent } from '../../models';
import { ReactNode, useEffect, useState } from 'react';

const VisualizationTypeItem = ({
  children,
  displayIcon,
}: {
  children: ReactNode;
  displayIcon: Icon.Type;
}): JSX.Element => {
  return (
    <div className="flex flex-row items-center gap-1">
      <Icon type={displayIcon} />
      {children}
    </div>
  );
};

interface VisualizationTypeSelectorProps {
  options: EditableComponent[];
  selected: VisualizationTypeOption;
  onSelectChange: (type: VisualizationTypeOption) => void;
}

export const VisualizationTypeSelector = ({
  options,
  selected,
  onSelectChange,
}: VisualizationTypeSelectorProps): JSX.Element => {
  const [currSelected, setCurrSelected] = useState(selected);

  useEffect(() => {
    if (selected !== currSelected) {
      setCurrSelected(selected);
    }
  }, [selected]);

  const onSelect = ([index]: number[], [_]: string[]) => {
    setCurrSelected(options[index].visualizationType);
    onSelectChange(options[index].visualizationType);
  };

  return (
    <div data-testid="dashify-widget-properties-panel-visualization-type-selector" className="flex flex-col gap-1">
      <div
        data-testid="dashify-widget-properties-panel-visualization-type-selector-label"
        style={{ font: 'var(--typography-p3-emphasis)' }}
      >
        Widget type
      </div>
      <Dropdown
        contentClassName={css({ width: 300 })}
        triggerClassName={css({
          paddingInline: 0,
          paddingTop: 16,
          paddingBottom: 16,
          paddingRight: 8,
          paddingLeft: 8,
          border: '2px solid var(--control-border)',
          color: 'var(--color-default-text-medium)',
          fontWeight: 'normal',
          borderRadius: 8,
        })}
        onSelect={onSelect}
        label={options.find(option => option.visualizationType === currSelected)?.displayName}
        kind="TERTIARY"
        icon={options.find(option => option.visualizationType === currSelected)?.displayIcon}
      >
        {options.map((option, index) => (
          <Dropdown.Option
            key={index}
            style={option.visualizationType === selected ? { backgroundColor: 'var(--interact-bg-weak-focus)' } : {}}
          >
            <VisualizationTypeItem {...option}>{option.displayName}</VisualizationTypeItem>
          </Dropdown.Option>
        ))}
      </Dropdown>
    </div>
  );
};
